/**
 * Confirming an upload session with the backend.
 *
 * Bytes go browser → Google Drive directly, so the backend only learns that a
 * session landed when the browser tells it via batch-complete. That call used
 * to be fire-and-forget (`.catch(() => {})`): if it failed, or never left the
 * machine, the files were unrecorded, nobody was notified, and the rows were
 * purged 24 h later — while the doctor was shown a green tick.
 *
 * So confirmation is treated as part of the upload, not an afterthought:
 * retried on transient failures, surfaced to the doctor when it cannot be
 * completed, and flushed via sendBeacon as a last resort if the page goes away
 * mid-retry.
 */

export const CONFIRM = {
  IDLE:        'idle',
  CONFIRMING:  'confirming',
  CONFIRMED:   'confirmed',
  UNCONFIRMED: 'unconfirmed',
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Is this failure worth retrying?
 *
 * No response at all (network drop, timeout, DNS, CORS) is the case we are
 * actually here for, so it retries. 5xx and 429 are transient server-side.
 * Any other 4xx means the request itself is wrong — a malformed payload or a
 * deleted case — and repeating it just wastes the doctor's time.
 */
export function isRetryableError(err) {
  const status = err?.response?.status
  if (status === undefined || status === null) return true
  if (status === 429) return true
  return status >= 500
}

/**
 * Calls `send` until it succeeds or the attempts run out, backing off
 * exponentially between tries.
 *
 * @param {object}   opts
 * @param {Function} opts.send        - performs one attempt; rejects on failure
 * @param {number}   [opts.attempts]  - total tries, including the first
 * @param {number}   [opts.baseDelayMs]
 * @param {Function} [opts.sleep]     - injectable for tests
 * @param {Function} [opts.onAttempt] - called with (attemptNumber, totalAttempts)
 * @returns {Promise<{ok: boolean, attempts: number, error?: Error}>}
 */
export async function confirmUploadWithRetry({
  send,
  attempts = 4,
  baseDelayMs = 1000,
  sleep = defaultSleep,
  onAttempt,
} = {}) {
  let lastError = null

  for (let attempt = 1; attempt <= attempts; attempt++) {
    if (onAttempt) onAttempt(attempt, attempts)
    try {
      await send()
      return { ok: true, attempts: attempt }
    } catch (err) {
      lastError = err
      if (!isRetryableError(err)) return { ok: false, attempts: attempt, error: err }
      if (attempt < attempts) await sleep(baseDelayMs * 2 ** (attempt - 1))
    }
  }

  return { ok: false, attempts, error: lastError }
}

/**
 * Last-ditch flush when the page is closing and confirmation never landed.
 *
 * The body is sent as text/plain deliberately: sendBeacon cannot perform a
 * CORS preflight, and application/json is not a safelisted content type, so a
 * JSON beacon to the API origin would be dropped without ever being sent. The
 * backend parses text/plain on this route for exactly this reason.
 *
 * @returns {boolean} true if the browser accepted the beacon for delivery
 */
export function sendConfirmBeacon({ url, payload, beacon }) {
  const send = beacon
    || (typeof navigator !== 'undefined' && navigator.sendBeacon
      ? navigator.sendBeacon.bind(navigator)
      : null)
  if (!send || !url) return false

  try {
    const body = new Blob([JSON.stringify(payload)], { type: 'text/plain;charset=UTF-8' })
    return send(url, body) === true
  } catch {
    return false
  }
}
